import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/admin/layout/AdminLayout';
import { ApplicationList } from '@/components/admin/application/ApplicationList';
import { DeleteApplicationDialog } from '@/components/admin/application/DeleteApplicationDialog';
import { ResetApplicationDialog } from '@/components/admin/application/ResetApplicationDialog';
import { ApplicationViewSheet } from '@/components/admin/application/ApplicationViewSheet';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { Application } from '@/types/database.types';
import { Json } from '@/integrations/supabase/types';
import { transformApplicationData } from '@/utils/application-data-utils';

// Extended type to include user profile information
interface ApplicationWithUser extends Application {
  user_email?: string;
  user_first_name?: string;
  user_last_name?: string;
}

const AdminApplicationsPage = () => {
  const [applications, setApplications] = useState<ApplicationWithUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  const [isViewSheetOpen, setIsViewSheetOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [isActionInProgress, setIsActionInProgress] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Fetch application data with user profile information
  const fetchApplications = async () => {
    console.log('AdminApplicationsPage: Starting to fetch applications...');
    setIsLoading(true);
    try {
      // First, let's try a simple query to test basic connectivity
      console.log('AdminApplicationsPage: Testing basic applications query...');
      const { data: testData, error: testError } = await supabase
        .from('applications')
        .select('id, company_legal_name, status, created_at, user_id')
        .limit(5);
      
      if (testError) {
        console.error('AdminApplicationsPage: Basic query failed:', testError);
        throw testError;
      }
      
      console.log('AdminApplicationsPage: Basic query successful, found', testData?.length, 'applications');
      
      // Now try the full query with profile join (using correct syntax)
      console.log('AdminApplicationsPage: Fetching applications with user profiles...');
      const { data, error } = await supabase
        .from('applications')
        .select(`
          *,
          profiles (
            email,
            first_name,
            last_name
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('AdminApplicationsPage: Full query failed:', error);
        throw error;
      }
      
      console.log('AdminApplicationsPage: Full query successful, raw data:', data?.length, 'applications');
      console.log('AdminApplicationsPage: Sample raw data:', data?.[0]);
      
      // Process the data to include user profile information and handle type conversions
      const processedApplications = data?.map((item, index) => {
        console.log(`AdminApplicationsPage: Processing application ${index + 1}/${data.length}:`, item.id);
        
        // Handle the profiles relationship - it should be a single object, not an array
        const profile = Array.isArray(item.profiles) ? item.profiles[0] : item.profiles;
        console.log('AdminApplicationsPage: Profile data for application:', item.id, profile);
        
        // Destructure to remove profiles and add user info
        const { profiles, ...applicationData } = item;
        
        try {
          // Use the comprehensive transformation utility
          const transformedApp = transformApplicationData(applicationData);
          console.log('AdminApplicationsPage: Successfully transformed application:', item.id);
          
          // Now safely add the user profile information
          const result: ApplicationWithUser = {
            ...transformedApp,
            user_email: profile?.email,
            user_first_name: profile?.first_name,
            user_last_name: profile?.last_name,
          };
          
          console.log('AdminApplicationsPage: Final application data:', item.id, {
            companyName: result.company_legal_name,
            userEmail: result.user_email,
            hasTransformedArrays: Array.isArray(result.desired_medical_carriers)
          });
          
          return result;
        } catch (transformError) {
          console.error('AdminApplicationsPage: Error transforming application:', item.id, transformError);
          // Use the transformation utility even for fallback case to ensure proper typing
          try {
            const transformedFallback = transformApplicationData(applicationData);
            const fallbackResult: ApplicationWithUser = {
              ...transformedFallback,
              user_email: profile?.email,
              user_first_name: profile?.first_name,
              user_last_name: profile?.last_name,
            };
            
            return fallbackResult;
          } catch (fallbackError) {
            console.error('AdminApplicationsPage: Even fallback transformation failed:', item.id, fallbackError);
            // Last resort: create a minimal application with required fields
            return {
              id: applicationData.id,
              user_id: applicationData.user_id || '',
              status: applicationData.status || 'draft',
              progress: applicationData.progress || 0,
              created_at: applicationData.created_at || new Date().toISOString(),
              updated_at: applicationData.updated_at || new Date().toISOString(),
              last_modified: applicationData.last_modified || new Date().toISOString(),
              company_legal_name: applicationData.company_legal_name,
              first_name: applicationData.first_name,
              last_name: applicationData.last_name,
              title: applicationData.title,
              phone: applicationData.phone,
              email: applicationData.email,
              // Initialize all required array/object fields as empty
              desired_medical_carriers: [],
              desired_ancillary_carriers: [],
              states_of_operation: [],
              states_with_employees: [],
              other_documents: [],
              health_plans: [],
              additional_contacts: [],
              additional_schedules: [],
              // Add user profile information
              user_email: profile?.email,
              user_first_name: profile?.first_name,
              user_last_name: profile?.last_name,
            } as ApplicationWithUser;
          }
        }
      });
      
      console.log('AdminApplicationsPage: Processing complete, setting', processedApplications?.length, 'applications');
      setApplications(processedApplications || []);
      
    } catch (error: any) {
      console.error('AdminApplicationsPage: Critical error fetching applications:', error);
      console.error('AdminApplicationsPage: Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      });
      
      toast({
        title: 'Error loading applications',
        description: error.message || 'Failed to load applications',
        variant: 'destructive',
      });
      
      // Set empty array so component doesn't stay in loading state
      setApplications([]);
    } finally {
      console.log('AdminApplicationsPage: Fetch complete, setting loading to false');
      setIsLoading(false);
    }
  };
  
  // Initial data fetch
  useEffect(() => {
    console.log('AdminApplicationsPage: Component mounted, starting data fetch...');
    fetchApplications();
  }, []);
  
  // Handler functions
  const handleViewApplication = (id: string) => {
    console.log('AdminApplicationsPage: Viewing application:', id);
    setSelectedAppId(id);
  };
  
  const handleResetRequest = (id: string) => {
    console.log('AdminApplicationsPage: Reset request for application:', id);
    setSelectedAppId(id);
    setIsResetDialogOpen(true);
  };
  
  const handleDeleteRequest = (id: string) => {
    console.log('AdminApplicationsPage: Delete request for application:', id);
    setSelectedAppId(id);
    setIsDeleteDialogOpen(true);
  };
  
  const handleResetApplication = async () => {
    if (!selectedAppId) return;
    
    console.log('AdminApplicationsPage: Resetting application:', selectedAppId);
    setIsActionInProgress(true);
    try {
      // Reset application logic
      await supabase
        .from('applications')
        .update({ 
          status: 'draft',
          progress: 0 
        })
        .eq('id', selectedAppId);
      
      toast({
        title: 'Application reset',
        description: 'The application has been reset to draft status.',
      });
      fetchApplications();
    } catch (error: any) {
      console.error('AdminApplicationsPage: Error resetting application:', error);
      toast({
        title: 'Error resetting application',
        description: error.message || 'Failed to reset application',
        variant: 'destructive',
      });
    } finally {
      setIsActionInProgress(false);
      setIsResetDialogOpen(false);
    }
  };
  
  const handleDeleteApplication = async () => {
    if (!selectedAppId) return;
    
    console.log('AdminApplicationsPage: Deleting application:', selectedAppId);
    setIsActionInProgress(true);
    setDeleteErrorMessage(null);
    try {
      // Delete application logic
      const { error } = await supabase
        .from('applications')
        .delete()
        .eq('id', selectedAppId);
      
      if (error) throw error;
      
      toast({
        title: 'Application deleted',
        description: 'The application has been permanently deleted.',
      });
      fetchApplications();
    } catch (error: any) {
      console.error('AdminApplicationsPage: Error deleting application:', error);
      setDeleteErrorMessage(error.message || 'Failed to delete application');
      toast({
        title: 'Error deleting application',
        description: error.message || 'Failed to delete application',
        variant: 'destructive',
      });
    } finally {
      setIsActionInProgress(false);
      setIsDeleteDialogOpen(false);
    }
  };
  
  const handleSubmitApplication = async (id: string) => {
    console.log('AdminApplicationsPage: Submitting application:', id);
    setIsActionInProgress(true);
    try {
      // Submit application logic
      await supabase
        .from('applications')
        .update({ 
          status: 'submitted' 
        })
        .eq('id', id);
      
      toast({
        title: 'Application submitted',
        description: 'The application has been successfully submitted.',
      });
      fetchApplications();
    } catch (error: any) {
      console.error('AdminApplicationsPage: Error submitting application:', error);
      toast({
        title: 'Error submitting application',
        description: error.message || 'Failed to submit application',
        variant: 'destructive',
      });
    } finally {
      setIsActionInProgress(false);
    }
  };
  
  const handleApplicationUpdate = async (id: string, data: Partial<Application>) => {
    console.log('AdminApplicationsPage: Updating application:', id, data);
    try {
      // Convert the data to a format that Supabase accepts
      // Handle array fields properly
      const updateData: Record<string, any> = { ...data };
      
      // Properly handle additional_contacts and additional_schedules
      if (Array.isArray(updateData.additional_contacts)) {
        updateData.additional_contacts = updateData.additional_contacts as unknown as Json;
      }
      
      if (Array.isArray(updateData.additional_schedules)) {
        updateData.additional_schedules = updateData.additional_schedules as unknown as Json[];
      }
      
      // Convert arrays to strings for these fields if they exist
      const arrayFields = [
        'desired_medical_carriers',
        'desired_ancillary_carriers',
        'other_documents',
        'states_of_operation',
        'states_with_employees',
        'top_3_peo_needs',
        'preferred_peos',
        'excluded_peos'
      ];
      
      arrayFields.forEach(field => {
        if (Array.isArray(updateData[field])) {
          updateData[field] = JSON.stringify(updateData[field]);
        }
      });
      
      await supabase
        .from('applications')
        .update(updateData)
        .eq('id', id);
      
      fetchApplications();
      return true;
    } catch (error) {
      console.error('AdminApplicationsPage: Error updating application:', error);
      return false;
    }
  };

  console.log('AdminApplicationsPage: Rendering with', applications.length, 'applications, loading:', isLoading);

  return (
    <AdminLayout>
      <ApplicationList
        applications={applications}
        isLoading={isLoading}
        onView={(id) => {
          handleViewApplication(id);
          setIsViewSheetOpen(true);
        }}
        onResetRequest={handleResetRequest}
        onDeleteRequest={handleDeleteRequest}
        onSubmit={handleSubmitApplication}
        isActionInProgress={isActionInProgress}
      />
      
      {/* Application View Sheet */}
      <ApplicationViewSheet 
        applicationId={selectedAppId}
        isOpen={isViewSheetOpen}
        onOpenChange={setIsViewSheetOpen}
      />
      
      {/* Reset Confirmation Dialog */}
      <ResetApplicationDialog
        isOpen={isResetDialogOpen}
        onOpenChange={setIsResetDialogOpen}
        onConfirm={handleResetApplication}
        isActionInProgress={isActionInProgress}
      />
      
      {/* Delete Confirmation Dialog */}
      <DeleteApplicationDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDeleteApplication}
        isActionInProgress={isActionInProgress}
        errorMessage={deleteErrorMessage}
      />
    </AdminLayout>
  );
};

export default AdminApplicationsPage;
